/** 화면 간 공유하는 TanStack Query 키 팩토리. */
export const runKeys = {
  all: ['runs'] as const,
  list: (query?: Record<string, unknown>) => ['runs', 'list', query ?? {}] as const,
  detail: (runId: string) => ['runs', 'detail', runId] as const,
  steps: (runId: string) => ['runs', 'steps', runId] as const,
  output: (runId: string) => ['runs', 'output', runId] as const,
  io: (runId: string) => ['runs', 'io', runId] as const,
};

export const graphKeys = {
  all: ['graphs'] as const,
  list: () => ['graphs', 'list'] as const,
  detail: (name: string) => ['graphs', 'detail', name] as const,
  /** 버전 목록 (지시48 ③). Save As 뒤에 이것만 무효화한다 — 상세를 건드리면 캔버스가 튄다. */
  versions: (name: string) => ['graphs', 'versions', name] as const,
  /** 지시193 — 지우면 무엇이 사라지나. 창을 열 때마다 새로 센다. */
  deletion: (name: string) => ['graphs', 'deletion', name] as const,
  /**
   * **그 판의 내용** (결정1451). `detail` 과 **같은 열쇠를 쓰면 안 된다** — 실행이 쓴
   * 판은 지금 편집 중인 판과 다르고, 섞이면 *"설계는 A 였다"* 를 **틀린 판**으로 말한다.
   */
  atVersion: (name: string, version: string) => ['graphs', 'detail', name, 'v', version] as const,
};

/** 지금 도는 서버 판 (결정1400). 한 번 받으면 잘 안 바뀐다 — 재기동해야 바뀐다. */
export const serverKeys = {
  version: () => ['server', 'version'] as const,
};

export const approvalKeys = {
  list: () => ['approvals', 'list'] as const,
};

export const fleetKeys = {
  queues: () => ['fleet', 'queues'] as const,
  /** 노드별 판 대조 (지시137, 결정6465). 큐와 다른 물음이라 열쇠를 겹치지 않는다. */
  builds: () => ['fleet', 'builds'] as const,
  nodes: (query?: Record<string, unknown>) => ['nodes', query ?? {}] as const,
  environments: () => ['environments'] as const,
  /** 그 노드에 매인 것 (지시179). 노드마다 다른 물음이라 id 를 열쇠에 넣는다. */
  bindings: (nodeId: string) => ['nodes', nodeId, 'bindings'] as const,
};

/** Dashboard 기술 통계 (지시188). 창·자기 점검 여부가 다르면 다른 물음이라 열쇠에 넣는다. */
export const statsKeys = {
  dashboard: (query?: Record<string, unknown>) => ['stats', 'dashboard', query ?? {}] as const,
  /** 파이프라인마다 운영 사항 (지시191). */
  pipelines: (query?: Record<string, unknown>) => ['stats', 'pipelines', query ?? {}] as const,
};

export const settingsKeys = {
  apiKeys: () => ['api-keys'] as const,
  deployTargets: () => ['deploy-targets'] as const,
  deployments: () => ['deployments'] as const,
  pipelineLimits: (pipeline: string) => ['pipeline-limits', pipeline] as const,
  globalStop: () => ['global-stop'] as const,
};

/**
 * 프로비저닝 대상 (지시42 ③).
 *
 * 세 화면이 같은 목록을 읽는데 키를 **손으로 세 번** 적고 있었다. 하나가 등록하고
 * 다른 하나가 낡은 목록을 보여 주는 것을 막으려면 무효화 대상이 같아야 한다.
 */
export const provisionKeys = {
  targets: () => ['provision', 'targets'] as const,
  /**
   * 그 기계의 GPU 재고 (결정6452).
   *
   * ⚠ **`targets()` 의 접두를 공유하지 않는다.** 대상 목록을 무효화한다고 해서 남의
   * 기계에 꽂힌 GPU 가 달라지지 않고, 반대로 이 값은 **대상 목록과 무관하게 낡는다**
   * (남이 그 GPU 를 쓰기 시작하면 낡는다). 접두를 겹치면 한쪽 무효화가 다른 쪽을
   * 끌고 다녀 «왜 다시 읽나» 를 아무도 설명 못 하게 된다.
   */
  gpus: (targetId: string) => ['provision', 'gpus', targetId] as const,
  /** 그 기계에서 우리가 올려 둔 컨테이너 (지시59, 결정6472). GPU 와 같은 까닭으로 접두를 안 겹친다. */
  containers: (targetId: string) => ['provision', 'containers', targetId] as const,
  /**
   * 그 기계의 **tailnet 판정** (지시70 ①, 결정8429). 형제들과 같은 까닭으로 접두를 안 겹친다.
   *
   * ⚠ 이 값은 **가입에 성공하면 곧바로 낡는다** — 그래서 `joinTailnet` 뒤에 이 열쇠를 무효화한다.
   *   안 하면 방금 넣은 기계를 창이 계속 «아직 없다» 로 그린다.
   */
  tailscale: (targetId: string) => ['provision', 'tailscale', targetId] as const,
  /**
   * 그 기계의 `.env` 키 이름들 (결정8441). 형제들과 같은 까닭으로 접두를 안 겹친다.
   *
   * ⚠ 이 값은 **그 기계에서 무엇이 돌든 낡는다** — 갖추기가 한 번 걸리면 곧바로 옛말이 된다.
   *   그래서 캐시를 믿고 오래 두면 «있다» 를 잘못 말한다. 사람이 펼 때 다시 읽는다.
   */
  nodeEnvKeys: (targetId: string) => ['provision', 'node-env-keys', targetId] as const,
};

/** 구성원·초대 (지시54). */
export const accessKeys = {
  members: () => ['access', 'members'] as const,
  /**
   * 제거된 구성원 (engine 결정1307). **`members()` 와 다른 키다** — 접두를 공유하므로
   * `['access']` 무효화가 둘 다 잡고, 되살리기 뒤 양쪽을 같이 되살려야 한다
   * (한쪽만 하면 그 사람이 두 목록에 동시에 뜨거나 어디에도 안 뜬다).
   */
  removedMembers: () => ['access', 'members', 'removed'] as const,
  /**
   * 감사 기록 (engine 결정1309).
   *
   * **`['access']` 접두를 공유한다** — 구성원을 고치면 그 행위가 감사에 새로 남으므로,
   * 구성원 쪽 무효화가 감사도 함께 되살리는 편이 맞다.
   */
  audit: () => ['access', 'audit'] as const,
  invites: () => ['access', 'invites'] as const,
};

export const resourceKeys = {
  credentials: () => ['resources', 'credentials'] as const,
  serviceKeys: () => ['resources', 'service-keys'] as const,
};

export const catalogKeys = {
  /**
   * 그 공급자의 서버에 실제로 있는 모델 (지시110, 결정6458).
   *
   * ⚠ `models()` 의 접두를 **안 겹친다.** 카탈로그를 다시 읽는다고 남의 서버에 깔린
   * 것이 달라지지 않고, 반대로 이 값은 카탈로그와 **무관하게** 낡는다(누가 그 기계에
   * 모델을 내려받으면 낡는다).
   */
  serverModels: (providerId: string) => ['catalog', 'server-models', providerId] as const,
  agentDefs: () => ['catalog', 'agent-defs'] as const,
  toolDefs: () => ['catalog', 'tool-defs'] as const,
  datasources: () => ['catalog', 'datasources'] as const,
  models: () => ['catalog', 'models'] as const,
  /** 등록된 LLM 공급자 (지시96 ③, 결정1455). 모델 목록과 **다른 물음**이다 — 이쪽만 노드를 안다. */
  llmProviders: () => ['catalog', 'llm-providers'] as const,
  /** 등록할 수 있는 에이전트 종류의 선언 (지시51 ②). 등록된 정의 목록과 다른 것이다. */
  agentKinds: () => ['catalog', 'agent-kinds'] as const,
};

export const paseoKeys = {
  agents: () => ['paseo', 'agents'] as const,
  /** 노드별 Paseo 세션(결정53). 노드마다 다른 캐시 항목이어야 섞이지 않는다. */
  nodeAgents: (nodeId: string) => ['paseo', 'node-agents', nodeId] as const,
  inspect: (id: string) => ['paseo', 'inspect', id] as const,
  /** tailnet 데몬 탐지(지시25). 접두사 'paseo' 를 공유해 부분일치 무효화가 함께 잡는다. */
  discovery: () => ['paseo', 'discovery'] as const,
  logs: (id: string) => ['paseo', 'logs', id] as const,
};

/**
 * 설계 저장소 (지시65).
 *
 * **인자를 더할 때 조심하라.** `list()` 와 `list({scope})` 는 **다른 키**이고,
 * 접두 일치로 무효화하는 쪽이 전부 안 닿는다(전임 인계 §5-8 이 이 자리에서 멈췄다).
 * 지금은 인자 없이 하나로 두고, 화면이 `scope` 로 거른다.
 */
export const designKeys = {
  all: () => ['designs'] as const,
  list: () => ['designs', 'list'] as const,
  detail: (designId: string) => ['designs', 'detail', designId] as const,
};


/** Database 화면 (지시220). 서버 → 데이터베이스 → 표 → 내용 차례로 열쇠가 좁아진다. */
export const databaseKeys = {
  servers: () => ['databases', 'servers'] as const,
  catalogs: (serverId: string) => ['databases', serverId, 'catalogs'] as const,
  tables: (serverId: string, database: string) => ['databases', serverId, database, 'tables'] as const,
  table: (serverId: string, database: string, schema: string, table: string) => ['databases', serverId, database, schema, table, 'meta'] as const,
  rows: (serverId: string, database: string, schema: string, table: string, query: Record<string, unknown>) =>
    ['databases', serverId, database, schema, table, 'rows', query] as const,
};
